import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { CalendarRange, Loader2, Mail, TrendingDown, TrendingUp } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";

const MONTHS = ["January", "February", "March", "April", "May", "June", "July",
  "August", "September", "October", "November", "December"];

const Delta = ({ mins }) => (
  <span className={`inline-flex items-center gap-1 text-xs font-medium ${
    mins >= 0 ? "text-emerald-600" : "text-rose-600"}`}>
    {mins >= 0 ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
    {Math.abs(mins)}m/day
  </span>
);

/** Month-end comparison: this month vs last, plus the biggest movers. */
export function ProdMonthlyPanel({ canManage }) {
  const now = new Date();
  const prev = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const [ym, setYm] = useState({ year: prev.getFullYear(), month: prev.getMonth() + 1 });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cfg, setCfg] = useState(null);
  const [recip, setRecip] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/productivity/monthly?year=${ym.year}&month=${ym.month}`)
      .then(({ data: d }) => setData(d))
      .catch((e) => toast.error(e.response?.data?.detail || "Could not load the month"))
      .finally(() => setLoading(false));
  }, [ym]);

  const loadCfg = () => api.get("/productivity/monthly/config").then(({ data: d }) => {
    setCfg(d);
    setRecip((d.config.recipients || []).join(", "));
  }).catch(() => {});

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadCfg(); }, []);

  const save = async () => {
    setBusy(true);
    try {
      const { data: d } = await api.put("/productivity/monthly/config",
        { enabled: !!cfg.config.enabled, recipients: recip });
      setRecip((d.config.recipients || []).join(", "));
      toast.success("Month-end report saved");
      loadCfg();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const sendNow = async () => {
    setBusy(true);
    try {
      const { data: r } = await api.post(`/productivity/monthly/send-now?year=${ym.year}&month=${ym.month}`);
      if (r.status === "sent") toast.success(`Month-end report sent to ${r.sent} recipient(s)`);
      else if (r.status === "paused") toast.warning("Outbound email is paused — nothing delivered");
      else toast.error(`Status: ${r.status}`);
      loadCfg();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Send failed");
    } finally { setBusy(false); }
  };

  const years = [now.getFullYear(), now.getFullYear() - 1];

  return (
    <div className="space-y-4" data-testid="prod-monthly-panel">
      <Card className="p-4">
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <CalendarRange className="h-4 w-4 text-slate-400 dark:text-slate-400" />
          <h3 className="text-base font-semibold text-slate-800 dark:text-slate-100">Month-end summary</h3>
          <div className="ml-auto flex gap-2">
            <select value={ym.month} data-testid="prod-monthly-month"
              onChange={(e) => setYm((s) => ({ ...s, month: Number(e.target.value) }))}
              className="rounded-md border border-slate-300 dark:border-slate-600 px-2 py-1 text-sm">
              {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
            </select>
            <select value={ym.year} data-testid="prod-monthly-year"
              onChange={(e) => setYm((s) => ({ ...s, year: Number(e.target.value) }))}
              className="rounded-md border border-slate-300 dark:border-slate-600 px-2 py-1 text-sm">
              {years.map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
        </div>

        {loading ? (
          <div className="flex h-32 items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-slate-300 dark:text-slate-500" /></div>
        ) : !data ? null : !data.people.length ? (
          <p className="py-10 text-center text-sm text-slate-400 dark:text-slate-400" data-testid="prod-monthly-empty">
            No tracked activity in {data.label}.
          </p>
        ) : (
          <>
            <p className="mb-3 text-xs text-slate-500 dark:text-slate-400" data-testid="prod-monthly-caption">
              <b className="text-slate-700 dark:text-slate-200">{data.label}</b> compared with {data.prev_label} ·{" "}
              {data.totals.hms} recorded across {data.totals.active_users} tracked staff
            </p>

            <div className="mb-4 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/60 p-3" data-testid="prod-monthly-movers">
              <div className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">Biggest movers</div>
              <div className="flex flex-wrap gap-x-5 gap-y-1.5">
                {data.movers.map((m) => (
                  <span key={m.key} className="text-sm text-slate-700 dark:text-slate-200" data-testid={`prod-monthly-mover-${m.key}`}>
                    {m.name} <Delta mins={m.delta_min} />
                  </span>
                ))}
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm" data-testid="prod-monthly-table">
                <thead>
                  <tr className="border-b border-slate-100 dark:border-slate-800 text-xs uppercase tracking-wide text-slate-400 dark:text-slate-400">
                    <th className="py-2 pr-3 font-semibold">Person</th>
                    <th className="py-2 pr-3 font-semibold">Team</th>
                    <th className="py-2 pr-3 font-semibold">Days</th>
                    <th className="py-2 pr-3 font-semibold">Productive</th>
                    <th className="py-2 pr-3 font-semibold">Avg/day</th>
                    <th className="py-2 pr-3 font-semibold">Target</th>
                    <th className="py-2 pr-3 font-semibold">Of target</th>
                    <th className="py-2 font-semibold">vs {data.prev_label}</th>
                  </tr>
                </thead>
                <tbody>
                  {[...data.people].sort((a, b) => b.avg_productive_seconds - a.avg_productive_seconds).map((p) => (
                    <tr key={p.key} className="border-b border-slate-50" data-testid={`prod-monthly-row-${p.key}`}>
                      <td className="py-2 pr-3 font-medium text-slate-800 dark:text-slate-100">{p.name}</td>
                      <td className="py-2 pr-3 text-xs text-slate-500 dark:text-slate-400">{p.team}</td>
                      <td className="py-2 pr-3 text-xs text-slate-500 dark:text-slate-400">{p.active_days}</td>
                      <td className="py-2 pr-3">{p.productive_hms}</td>
                      <td className="py-2 pr-3">{p.avg_productive_hms}</td>
                      <td className="py-2 pr-3 text-slate-500 dark:text-slate-400">{p.goal_hms}</td>
                      <td className={`py-2 pr-3 font-semibold ${
                        p.goal_pct >= 100 ? "text-emerald-600" : p.goal_pct >= 80 ? "text-amber-600" : "text-rose-600"}`}>
                        {p.goal_pct}%
                      </td>
                      <td className="py-2"><Delta mins={p.delta_min} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </Card>

      {canManage && cfg && (
        <Card className="p-4" data-testid="prod-monthly-config">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-100">Email it automatically</h3>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                Sent on the 1st at 08:30 America/Edmonton, covering the month that just finished.
              </p>
            </div>
            <Switch checked={!!cfg.config.enabled} data-testid="prod-monthly-enabled"
              onCheckedChange={(v) => setCfg((c) => ({ ...c, config: { ...c.config, enabled: v } }))} />
          </div>
          <label className="mt-3 block text-xs text-slate-600 dark:text-slate-300">
            Recipients
            <Textarea value={recip} onChange={(e) => setRecip(e.target.value)} rows={2}
              data-testid="prod-monthly-recipients" placeholder="reo@unitedcloud.ca" className="mt-1 text-sm" />
          </label>
          <p className="mt-1 text-[11px] text-amber-700 dark:text-amber-300">
            Tracked staff can&apos;t be added — reports go to managers only.
          </p>
          {cfg.last_send && (
            <div className="mt-2 text-xs text-slate-500 dark:text-slate-400" data-testid="prod-monthly-last-send">
              Last send: {cfg.last_send.sent_at?.slice(0, 16).replace("T", " ")} UTC ·{" "}
              {cfg.last_send.status} · {cfg.last_send.label}
            </div>
          )}
          <div className="mt-3 flex gap-2">
            <Button size="sm" variant="outline" onClick={sendNow} disabled={busy} data-testid="prod-monthly-send-now">
              {busy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Mail className="mr-1 h-4 w-4" />}
              Send {MONTHS[ym.month - 1]} now
            </Button>
            <Button size="sm" onClick={save} disabled={busy} data-testid="prod-monthly-save">Save</Button>
          </div>
        </Card>
      )}
    </div>
  );
}
